import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaTimes } from "react-icons/fa";
import AdminDocumentUploader from "../AdminDocumentUploader";

export default function AdminCustomActivityModal({ onClose, onCreated }) {
  const [formData, setFormData] = useState({
    userId: "",
    countryId: "",
    cityId: "",
    activityId: "",
    activityOptionId: "",
    slotId: "",
    selectedDateTime: "",
    visitors: [{ type: "Adult", count: 1 }],
    totalAmount: "",
    status: "confirmed",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
  });

  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [options, setOptions] = useState([]);
  const [slots, setSlots] = useState([]);

  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [newBookingId, setNewBookingId] = useState(null);

  const BASE = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000";

  useEffect(() => {
    fetchUsers();
    fetchCountries();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${BASE}/api/user?limit=1000`, { withCredentials: true });
      if (res.data.success) {
        setUsers(res.data.data.users || res.data.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await axios.get(`${BASE}/api/activity/admin/countries`, { withCredentials: true });
      if (res.data.success) setCountries(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCities = async (countryId) => {
    if (!countryId) return setCities([]);
    try {
      const res = await axios.get(`${BASE}/api/activity/admin/cities/${countryId}`, { withCredentials: true });
      if (res.data.success) setCities(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchActivities = async (cityId) => {
    if (!cityId) return setActivities([]);
    try {
      const res = await axios.get(`${BASE}/api/activity/admin/activities/${cityId}`, { withCredentials: true });
      if (res.data.success) setActivities(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchOptions = async (activityId) => {
    if (!activityId) return setOptions([]);
    try {
      const res = await axios.get(`${BASE}/api/activity/admin/activity-options/${activityId}`, { withCredentials: true });
      if (res.data.success) setOptions(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSlots = async (optionId) => {
    if (!optionId) return setSlots([]);
    try {
      const res = await axios.get(`${BASE}/api/activity/admin/time-slots/${optionId}`, { withCredentials: true });
      if (res.data.success) setSlots(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === "userId" && value) {
      const selectedUser = users.find(u => u._id === value);
      if (selectedUser) {
        setFormData(prev => ({
          ...prev,
          customerName: selectedUser.name || selectedUser.username || "",
          customerEmail: selectedUser.email || "",
          customerPhone: selectedUser.phone || "",
        }));
      }
    }

    if (name === "countryId") fetchCities(value);
    if (name === "cityId") fetchActivities(value);
    if (name === "activityId") fetchOptions(value);
    if (name === "activityOptionId") {
      fetchSlots(value);
      const selectedOption = options.find(o => o._id === value);
      if (selectedOption && selectedOption.pricingTiers?.length > 0) {
        setFormData(prev => ({
          ...prev,
          visitors: selectedOption.pricingTiers.map(t => ({
            type: t.type,
            count: 0
          })),
          totalAmount: ""
        }));
      } else {
        setFormData(prev => ({ ...prev, visitors: [{ type: "Adult", count: 1 }], totalAmount: "" }));
      }
    }
  };

  const handleVisitorChange = (index, val) => {
    setFormData(prev => {
      const newVisitors = [...prev.visitors];
      newVisitors[index].count = Math.max(0, parseInt(val) || 0);
      return { ...prev, visitors: newVisitors };
    });
  };

  const calculatePrice = async () => {
    if (!formData.activityOptionId) return alert("Please select a Package (Option)");
    if (!formData.selectedDateTime) return alert("Please select a Journey Date");
    
    const totalVisitors = formData.visitors.reduce((sum, v) => sum + v.count, 0);
    if (totalVisitors === 0) return alert("Please add at least one visitor");

    let dateTime = new Date(`${formData.selectedDateTime}T00:00:00`);
    if (formData.slotId) {
      const slot = slots.find(s => s._id === formData.slotId);
      if (slot) dateTime = new Date(`${formData.selectedDateTime}T${slot.startTime}:00`);
    }

    setCalculating(true);
    try {
      const payload = {
        activityOptionId: formData.activityOptionId,
        visitors: formData.visitors.filter(v => v.count > 0).map(v => ({ type: v.type, count: v.count })),
        selectedDateTime: dateTime.toISOString(),
      };
      const res = await axios.post(`${BASE}/api/activity/user/calculate-price`, payload, { withCredentials: true });
      if (res.data.success) {
        setFormData(prev => ({ ...prev, totalAmount: res.data.data.total }));
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to calculate price. Ensure pricing is configured.");
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const selectedActivity = activities.find(a => a._id === formData.activityId);
      const selectedCity = cities.find(c => c._id === formData.cityId);
      
      const payload = {
        userId: formData.userId,
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        activityOptionId: formData.activityOptionId,
        slotId: formData.slotId || null,
        selectedDateTime: formData.selectedDateTime,
        visitors: formData.visitors,
        totalAmount: formData.totalAmount,
        currency: "INR",
        status: formData.status,
        paymentMethod: "card",
        isCustomActivity: true, // Flag to allow admin creation bypassing payment
        activityName: selectedActivity?.name || "Custom Activity",
        cityName: selectedCity?.name || "N/A"
      };

      const res = await axios.post(`${BASE}/api/activity/admin/custom-booking`, payload, { withCredentials: true });
      if (res.data.success) {
        setNewBookingId(res.data.data._id || res.data.bookingId || res.data.data.bookingId);
        alert("Custom activity booking created successfully!");
      }
    } catch (error) {
      alert(error.response?.data?.error || error.response?.data?.message || "Failed to create custom booking");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-800">
          <FaTimes size={20} />
        </button>
        <h2 className="text-xl font-bold mb-4">Create Custom Activity Booking</h2>

        {!newBookingId ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Select User</label>
              <select required name="userId" value={formData.userId} onChange={handleChange} className="w-full border p-2 rounded mb-3">
                <option value="">-- Select User --</option>
                {users.map(u => (
                  <option key={u._id} value={u._id}>{u.username} ({u.email})</option>
                ))}
              </select>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="col-span-1 md:col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Customer Name *</label>
                  <input type="text" required name="customerName" value={formData.customerName} onChange={handleChange} className="w-full border p-1.5 rounded text-sm" placeholder="Full Name" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Customer Email *</label>
                  <input type="email" required name="customerEmail" value={formData.customerEmail} onChange={handleChange} className="w-full border p-1.5 rounded text-sm" placeholder="Email Address" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Customer Phone *</label>
                  <input type="text" required name="customerPhone" value={formData.customerPhone} onChange={handleChange} className="w-full border p-1.5 rounded text-sm" placeholder="Phone Number" />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Country</label>
              <select required name="countryId" value={formData.countryId} onChange={handleChange} className="w-full border p-2 rounded">
                <option value="">-- Select Country --</option>
                {countries.map(c => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">City</label>
              <select required name="cityId" value={formData.cityId} onChange={handleChange} className="w-full border p-2 rounded" disabled={!formData.countryId}>
                <option value="">-- Select City --</option>
                {cities.map(c => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Activity (Attraction)</label>
              <select required name="activityId" value={formData.activityId} onChange={handleChange} className="w-full border p-2 rounded" disabled={!formData.cityId}>
                <option value="">-- Select Activity --</option>
                {activities.map(a => (
                  <option key={a._id} value={a._id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Package (Option)</label>
              <select required name="activityOptionId" value={formData.activityOptionId} onChange={handleChange} className="w-full border p-2 rounded" disabled={!formData.activityId}>
                <option value="">-- Select Package --</option>
                {options.map(o => (
                  <option key={o._id} value={o._id}>{o.name} - ₹{o.basePrice || 0}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Time Slot (Optional)</label>
              <select name="slotId" value={formData.slotId} onChange={handleChange} className="w-full border p-2 rounded" disabled={!formData.activityOptionId}>
                <option value="">-- No Slot / Flexible --</option>
                {slots.map(s => (
                  <option key={s._id} value={s._id}>{s.startTime} to {s.endTime}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Journey Date *</label>
                <input required type="date" name="selectedDateTime" value={formData.selectedDateTime} onChange={handleChange} className="w-full border p-2 rounded" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Visitors *</label>
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg border">
                  {formData.visitors.map((v, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="font-medium text-gray-700">{v.type}</span>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => handleVisitorChange(idx, v.count - 1)} className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300">-</button>
                        <input type="number" min="0" value={v.count} onChange={(e) => handleVisitorChange(idx, e.target.value)} className="w-16 text-center border rounded p-1" />
                        <button type="button" onClick={() => handleVisitorChange(idx, v.count + 1)} className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300">+</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-2">
              <button type="button" onClick={calculatePrice} disabled={calculating} className="bg-gray-800 text-white px-4 py-2 rounded font-bold hover:bg-gray-900 shadow text-sm">
                {calculating ? "Calculating..." : "Calculate Final Amount"}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 col-span-2">
                <label className="block text-sm font-medium mb-1 text-gray-700">Final Amount (Auto-Calculated or Admin Override) (₹) *</label>
                <input required type="number" name="totalAmount" value={formData.totalAmount} onChange={handleChange} className="w-full border p-2 rounded text-lg font-bold text-blue-700" placeholder="0.00" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Status</label>
                <select name="status" value={formData.status} onChange={handleChange} className="w-full border p-2 rounded">
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white p-2 rounded font-bold hover:bg-blue-700">
              {loading ? "Creating..." : "Create Booking"}
            </button>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 text-green-800 rounded">
              Booking Created Successfully!
            </div>
            <AdminDocumentUploader
              type="activity"
              bookingId={newBookingId}
              onUploadSuccess={(docs) => {
                // optionally do something with docs
              }}
            />
            <button onClick={() => { onCreated(); onClose(); }} className="w-full bg-gray-200 text-gray-800 p-2 rounded font-bold hover:bg-gray-300 mt-4">
              Finish & Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
